import {
  formattedDateForSubscription,
  globalToastManagerUI,
  isOptionStrategy,
  type Security,
  type SideEnum,
} from '@talos/kyoko';
import { baseRestApi, createTag } from '../../../utils/rtkQuery';
import type { MarginCostResponse } from './models';

export interface MarginCostRequestParams {
  security: Security;
  quantity: string;
  side: SideEnum;
  marketAccounts: string[];

  currency?: string;
  price?: string;
}

const orderSliceApiInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    getMarginCost: builder.query<MarginCostResponse[], MarginCostRequestParams>({
      // We use queryFn instead of query to be able to attach a ValidUntil property on the received records before ingesting
      async queryFn(params, queryApi, extraOptions, fetchWithBaseQuery) {
        const url = getMarginCostURLWithQueryParams(params);
        const result = await fetchWithBaseQuery({ url, method: 'GET' });

        if (result.error) {
          globalToastManagerUI.addToast({
            text: `Something went wrong when requesting margin cost`,
            variant: 'NEGATIVE',
          });
          return { error: result.error };
        }

        const data = result.data as MarginCostResponse[];
        const now = new Date();
        // ValidUntil is a UI only construct used in the PortfolioMargin case. When ValidUntil passes, the user is then allowed to redo this request manually by clicking a button.
        const validUntil = formattedDateForSubscription(now.setSeconds(now.getSeconds() + 10));
        return { data: data.map(dp => ({ ...dp, ValidUntil: validUntil })) };
      },
      // This associates these tags with each request. This lets us invalidate specific tags. The id below
      // is the obvious unique tag which we use to invalidate cache entries for specific requests.
      providesTags: (_result, _error, params) => [createTag('MarginCost', getMarginCostURLWithQueryParams(params))],
    }),
  }),
});

export const { useGetMarginCostQuery } = orderSliceApiInternal;
export const orderSliceApi = {
  endpoints: orderSliceApiInternal.endpoints,
  util: orderSliceApiInternal.util,
};

/** Goes from an object of parameters into a url query string to use to make a request */
export function getMarginCostURLWithQueryParams(params: MarginCostRequestParams): string {
  const marketAccounts = params.marketAccounts.join(',');

  const queryParams = new URLSearchParams();
  queryParams.append('Symbol', params.security.Symbol);
  queryParams.append('OrderQty', encodeURIComponent(params.quantity));
  queryParams.append('Side', params.side);
  if (params.currency) {
    queryParams.append('Currency', params.currency);
  }
  if (!isOptionStrategy(params.security)) {
    queryParams.append('MarketAccounts', marketAccounts);
  }
  if (params.price) {
    queryParams.append('Price', encodeURIComponent(params.price));
  }

  return `/orders/margin-cost?${queryParams.toString()}`;
}
